package com.sddamico.state

import androidx.compose.runtime.*
import com.sddamico.api.ApiException
import com.sddamico.api.SuccessResponse
import com.sddamico.api.apiGet
import com.sddamico.api.apiJson
import com.sddamico.api.apiPost
import com.sddamico.models.MeResponse
import com.sddamico.models.PushSubscribeRequest
import com.sddamico.models.SendOtpRequest
import com.sddamico.models.VapidKeyResponse
import com.sddamico.models.VerifyRequest
import com.sddamico.push.getCurrentSubscription
import com.sddamico.push.isPushSupported
import com.sddamico.push.getPermissionState
import com.sddamico.push.registerServiceWorker
import com.sddamico.push.subscribeToPush
import kotlinx.serialization.encodeToString

data class AuthState(
    val user: MeResponse? = null,
    val loading: Boolean = true,
)

class AuthActions(
    val sendOtp: suspend (email: String) -> Unit,
    val verify: suspend (email: String, code: String) -> Unit,
    val logout: suspend () -> Unit,
    val refresh: suspend () -> Unit,
)

val LocalAuthState = staticCompositionLocalOf { AuthState() }
val LocalAuthActions = staticCompositionLocalOf<AuthActions> {
    error("No AuthActions provided")
}

@Composable
fun AuthStateProvider(content: @Composable () -> Unit) {
    var authState by remember { mutableStateOf(AuthState()) }

    val actions = remember {
        AuthActions(
            sendOtp = { email ->
                val body = apiJson.encodeToString(SendOtpRequest(email))
                apiPost("/api/auth/send-otp", body, SuccessResponse.serializer())
            },
            verify = { email, code ->
                val body = apiJson.encodeToString(VerifyRequest(email, code))
                apiPost("/api/auth/verify", body, SuccessResponse.serializer())
                // Refresh user after successful verify
                try {
                    val user = apiGet("/api/auth/me", MeResponse.serializer())
                    authState = AuthState(user = user, loading = false)
                } catch (_: Exception) {
                    authState = AuthState(user = null, loading = false)
                }
            },
            logout = {
                try {
                    apiPost("/api/auth/logout", null, SuccessResponse.serializer())
                } catch (_: Exception) {
                    // Ignore errors during logout
                }
                authState = AuthState(user = null, loading = false)
            },
            refresh = {
                try {
                    val user = apiGet("/api/auth/me", MeResponse.serializer())
                    authState = AuthState(user = user, loading = false)
                } catch (_: Exception) {
                    authState = AuthState(user = null, loading = false)
                }
            },
        )
    }

    LaunchedEffect(Unit) {
        try {
            val user = apiGet("/api/auth/me", MeResponse.serializer())
            authState = AuthState(user = user, loading = false)

            // Skip push subscription refresh during impersonation
            if (user.isImpersonating) return@LaunchedEffect

            // Register service worker and refresh push subscription if already granted
            if (isPushSupported()) {
                try {
                    registerServiceWorker()
                    if (getPermissionState() == "granted") {
                        val vapidKey = apiGet("/api/push/vapid-key", VapidKeyResponse.serializer())
                        val sub = subscribeToPush(vapidKey.publicKey)
                        if (sub != null) {
                            val body = apiJson.encodeToString(PushSubscribeRequest(sub.endpoint, sub.p256dh, sub.auth))
                            apiPost("/api/push/subscribe", body, SuccessResponse.serializer())
                        }
                    }
                } catch (_: Throwable) {
                    // Push registration is best-effort — catch Throwable for JS exceptions
                }
            }
        } catch (_: Throwable) {
            authState = AuthState(user = null, loading = false)
        }
    }

    CompositionLocalProvider(
        LocalAuthState provides authState,
        LocalAuthActions provides actions,
    ) {
        content()
    }
}
